import { format, isAfter, isBefore } from 'date-fns';
import { getProperty } from '@ozgene/gp5.utils.object-functions';

/**
 *
 * @param {Date} value
 * @param {Object} filter Filter object
 * @param {Date []} filter.value Date array containing a start and finish date
 * @returns {Boolean} Value date falls within the start and finish dates
 */
export const filterDateBetween = (value, filter) => {
  const start = new Date(filter.value[0]);
  const finish = new Date(filter.value[1]);
  const valueDate = new Date(value);

  return (
    isBefore(start, finish) &&
    isAfter(valueDate, start) &&
    isBefore(valueDate, finish)
  );
};

/**
 *
 * @param {Date} value
 * @param {Object} filter Filter object with a value date
 * @param {Date} filter.value Date value of the filter object
 * @returns {Boolean} Value date has the same month value as the filter date
 */
export const dateFilterMonthEquals = (value, filter) => {
  const month = value.split('-')[1];
  const filterMonth = filter.split(['-'])[1];

  return filterMonth === month;
};

/**
 *
 * @param {Date} value
 * @param {Object} filter Filter object with a value date
 * @param {Date} filte.date Date value of the filter object
 * @returns {Boolean} Value date has the same year value as the filter date
 */
export const dateFilterYearEquals = (value, filter) => {
  const year = value.split(['-'])[0];

  return filter.includes(year);
};

/**
 *
 * @param {Date} value
 * @param {Object} filter Filter object with a value date
 * @param {Date} filter.value Date value of the filter object
 * @returns {Boolean} Value date is equal to the filter value date
 */
export const dateFilterEquals = (value, filter) =>
  format(new Date(value), 'yyyy-MM-dd').includes(filter);

/**
 *
 * @param {Date} value
 * @param {Object} filter Filter object with a value date
 * @param {Date} filter.value Date value of the filter object
 * @returns {Boolean} Value date occurs before the filter value date
 */
export const dateFilterLessThan = (value, filter) =>
  isBefore(new Date(value), new Date(filter));

/**
 *
 * @param {Date} value
 * @param {Object} filter Filter object with a value date
 * @param {Date} filter.value Date value of the filter object
 * @returns {Boolean} Value date occurs after the filter value date
 */
export const dateFilterGreaterThan = (value, filter) =>
  isAfter(new Date(value), new Date(filter));

/**
 *
 * @param {Func} getFilterValue Function to deconstruct the value from a value object
 * @param {String} value
 * @param {Object} filter Filter object
 * @param {String} filter.value
 * @returns {Boolean} value is equal to the filter value
 */
export const getGroupFilterEquals = (getFilterValue) => (value, filter) =>
  value === getFilterValue(filter.value);

/**
 *
 * @param {number} value
 * @param {Object} filter Filter object containing an array of 2 filter values, max and min
 * @param {number []} filter.value Value array containing th emax and min values
 * @returns {Boolean} value int falls between max and min filter values
 */
export const filterIntegerBetween = (value, filter) => {
  const val = parseInt(value);
  const min = parseInt(filter.value[0]);
  const max = parseInt(filter.value[1]);

  return (
    ((isNaN(min) && isNaN(max)) || !isNaN(val)) &&
    (isNaN(min) || val >= min) &&
    (isNaN(max) || val <= max)
  );
};

/**
 *
 * @param {function|String} property Property getter function or String name
 * @param {Array} value Array of values containing the required property
 * @param {Object} filter Filter object
 * @param {String} filter.value Value of the filter object
 * @returns {Boolean} value property includes filter value
 */
export const getFilterArrayForProperty = (property) => (value, filter) =>
  value.some((item) =>
    getProperty(item, property)
      .toUpperCase()
      .trim()
      .includes(filter.value.toString().toUpperCase().trim())
  );

/**
 *
 * @param {String} value
 * @param {Object} filter Filter object with a value
 * @param {String} filter.value Value of the filter object
 * @returns {Boolean} Filter Value includes value
 */
export const filterIncludes = (value, filter) => filter.value.includes(value);

/**
 *
 * @param {String, number} value Can be either String or number
 * @param {Object} filter Filter object with a value
 * @param {String} filter.value Value of the filter object
 * @returns {Boolean} Filter value = value
 */
export const filterEquals = (value, filter) => filter.value === value;

/**
 * Gets a filter function that validates a boolean property
 * in the row value against the provided option values for all/true/false
 *
 * @param {String} allOptionValue The value in the filter options that matches all values
 * @param {String} trueOptionValue The value in the filter options that matches true values
 * @param {String} falseOptionValue The value in the filter options that matches false values
 * @returns {Function} A filter to be used on boolean properties
 */
export const getBooleanToggleFilter =
  (allOptionValue, trueOptionValue, falseOptionValue) => (value, filter) =>
    filter.value === allOptionValue ||
    (value
      ? filter.value === trueOptionValue
      : filter.value === falseOptionValue);
